import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { sanitizeText } from './lessonText';

const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

const isItemSelected = (list, opt) => {
    if (!Array.isArray(list)) return false;
    const optLower = typeof opt === 'string' ? opt.toLowerCase() : opt;
    return list.some(i => {
        if (i === opt) return true;
        if (typeof i === 'string' && typeof optLower === 'string') {
            const iLower = i.toLowerCase();
            return iLower === optLower || iLower.startsWith(optLower + ':');
        }
        return false;
    });
};

const getSelectedOptions = (list, options) => {
    if (!Array.isArray(list)) return [];
    return options.filter(opt => isItemSelected(list, opt));
};

let cachedLogoBase64 = null;
const getLogoBase64 = async () => {
    if (cachedLogoBase64) return cachedLogoBase64;
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2500);
        const logoRes = await fetch('/kianda-school-logo-removebg-preview.png', { signal: controller.signal });
        clearTimeout(timeoutId);
        const logoBlob = await logoRes.blob();
        cachedLogoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(logoBlob);
        });
        return cachedLogoBase64;
    } catch (e) {
        console.warn("Could not load logo for PDF", e);
        return null;
    }
};

// "Kiswahili - Grade 10 (K & S)": the learning area and the class(es) a plan or record applies to
const buildClassLabel = (item) => {
    const area = item?.learning_area_name || '';
    const grade = item?.grade_level_name || '';
    const stream = item?.stream_name || '';
    const gradePart = grade ? `${grade}${stream ? ` (${stream})` : ''}` : '';
    return [area, gradePart].filter(Boolean).join(' - ');
};

// First header line: the week sits to the left of the date ("Week 5   Date: 2026-10-07")
const buildWeekDateLine = (weekNumber, date) => `${weekNumber ? `Week ${weekNumber}   ` : ''}Date: ${date}`;

const BRAND_PRIMARY = [24, 33, 109]; // #18216D
const BRAND_ACCENT = [255, 196, 37]; // #FFC425
const PAGE_MARGIN = 20;

// Header shared by the lesson plan and record of work PDFs: logo, school name and document label on the
// left, detail lines on the right. Returns the y position where the body should start.
const drawLessonDocHeader = async (doc, label, detailLines) => {
    const pageWidth = doc.internal.pageSize.width;
    const rightAlign = pageWidth - PAGE_MARGIN;

    try {
        const logoBase64 = await getLogoBase64();
        if (logoBase64) {
            doc.addImage(logoBase64, 'PNG', PAGE_MARGIN, 15, 20, 20);
        }
    } catch (e) {
        console.error("Failed to load logo for PDF", e);
    }

    // Header Text (Beside Logo)
    doc.setTextColor(...BRAND_PRIMARY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("KIANDA SCHOOL", PAGE_MARGIN + 25, 25);

    doc.setTextColor(...BRAND_ACCENT);
    doc.setFontSize(10);
    doc.text(label, PAGE_MARGIN + 25, 33);

    // Header details (right aligned); long lines wrap and the next line moves down
    doc.setTextColor(100, 100, 100);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    let y = 15;
    detailLines.filter(Boolean).forEach(line => {
        const wrapped = doc.splitTextToSize(line, 80);
        doc.text(wrapped, rightAlign, y, { align: 'right' });
        y += wrapped.length * 5;
    });

    return Math.max(50, y + 5);
};

// Writes titled sections one after another. `ctx` = { doc, y, margin, maxWidth } and `ctx.y` is advanced.
const addSection = (ctx, title, content, isHtml = false) => {
    const { doc, margin, maxWidth } = ctx;
    if (!content || (Array.isArray(content) && content.length === 0)) return;

    // Add new page if close to bottom
    if (ctx.y > 260) {
        doc.addPage();
        ctx.y = 20;
    }

    // Section Title
    doc.setTextColor(...BRAND_PRIMARY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(title, margin, ctx.y);
    ctx.y += 8;

    // Section Content
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    let textLines = [];
    if (Array.isArray(content)) {
        textLines = content.flatMap(item => doc.splitTextToSize(`• ${item}`, maxWidth));
    } else {
        const rawText = isHtml ? stripHtml(content) : content;
        textLines = doc.splitTextToSize(rawText, maxWidth);
    }

    doc.text(textLines, margin, ctx.y);
    ctx.y += (textLines.length * 5) + 10;
};

// Learning outcomes can be stored as a list or as newline separated text; both become a clean list
const toOutcomeList = (outcomes) => {
    const items = Array.isArray(outcomes) ? outcomes : String(outcomes || '').split('\n');
    return items.map(o => stripHtml(String(o)).trim()).filter(Boolean);
};

export const generateLessonPlanPDF = async (formData) => {
    try {
        const doc = new jsPDF();

        const primaryColor = BRAND_PRIMARY;
        const margin = PAGE_MARGIN;
        const pageWidth = doc.internal.pageSize.width;
        const maxWidth = pageWidth - (margin * 2);

        const startY = await drawLessonDocHeader(doc, "LESSON PLAN", [
            buildWeekDateLine(formData.week_number, formData.lesson_date),
            buildClassLabel(formData) ? `Class: ${buildClassLabel(formData)}` : '',
            `Strand: ${formData.strand_name || 'N/A'}`,
            `Sub-strand: ${formData.sub_strand_name || 'N/A'}`
        ]);
        const ctx = { doc, y: startY, margin, maxWidth };

        // Body Title
        doc.setTextColor(...primaryColor);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        const titleLines = doc.splitTextToSize(formData.title || 'Untitled Lesson Plan', maxWidth);
        doc.text(titleLines, margin, ctx.y);
        ctx.y += (titleLines.length * 6) + 5;

        // Sections
        addSection(ctx, "Learning Outcomes", toOutcomeList(formData.custom_outcomes));
        addSection(ctx, "Key Inquiry Questions", formData.key_inquiry_questions, true);
        addSection(ctx, "Resources Needed", formData.resources_needed, true);
        addSection(ctx, "Introduction", formData.introduction, true);
        addSection(ctx, "Lesson Activities", formData.development, true);
        addSection(ctx, "Conclusion", formData.conclusion, true);

        const coreCompetenciesOptions = [
            "Communication and Collaboration",
            "Critical Thinking and Problem Solving",
            "Creativity and Imagination",
            "Citizenship",
            "Digital Literacy",
            "Learning to Learn",
            "Self-Efficacy"
        ];
        const valuesOptions = [
            "Responsibility",
            "Respect",
            "Integrity",
            "Unity",
            "Peace",
            "Patriotism"
        ];
        const pciOptions = [
            "Environmental Conservation",
            "Health and Safety",
            "Financial Literacy",
            "Digital Citizenship",
            "Gender and Inclusion",
            "Peace Education",
            "Life Skills"
        ];

        const selectedCompetencies = getSelectedOptions(formData.core_competencies, coreCompetenciesOptions);
        const selectedValues = getSelectedOptions(formData.values, valuesOptions);
        const selectedPCIs = getSelectedOptions(formData.pcis, pciOptions);

        if (selectedCompetencies.length > 0 || selectedValues.length > 0 || selectedPCIs.length > 0) {
            if (ctx.y > 240) {
                doc.addPage();
                ctx.y = 20;
            }

            const colWidth = maxWidth / 3;
            const startX1 = margin;
            const startX2 = margin + colWidth;
            const startX3 = margin + colWidth * 2;

            doc.setTextColor(...primaryColor);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text("Core Pillars", margin, ctx.y);
            ctx.y += 8;

            doc.setFontSize(10);
            doc.text("Competencies", startX1, ctx.y);
            doc.text("Values", startX2, ctx.y);
            doc.text("PCIs", startX3, ctx.y);

            ctx.y += 6;

            doc.setTextColor(60, 60, 60);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);

            const lines1 = selectedCompetencies.map(item => `• ${item}`);
            const lines2 = selectedValues.map(item => `• ${item}`);
            const lines3 = selectedPCIs.map(item => `• ${item}`);

            doc.text(lines1, startX1, ctx.y);
            doc.text(lines2, startX2, ctx.y);
            doc.text(lines3, startX3, ctx.y);

            const maxLines = Math.max(lines1.length, lines2.length, lines3.length, 1);
            ctx.y += (maxLines * 5) + 8;
        }

        addSection(ctx, "Assessment Strategies", formData.assessment_strategies, true);
        addSection(ctx, "Differentiation / Inclusivity", formData.inclusivity, true);
        addSection(ctx, "Reflection", formData.reflection, true);

        doc.save(`Lesson_Plan_${formData.lesson_date || 'Export'}.pdf`);
    } catch (err) {
        console.error("PDF Export Error: ", err);
    }
};

// Record of work: what was taught and the teacher's reflection. Same header as the lesson plan, fewer sections.
export const generateRecordOfWorkPDF = async (record) => {
    try {
        const doc = new jsPDF();

        const margin = PAGE_MARGIN;
        const maxWidth = doc.internal.pageSize.width - (margin * 2);

        const startY = await drawLessonDocHeader(doc, "RECORD OF WORK", [
            buildWeekDateLine(record.week_number, record.lesson_date || 'Unscheduled'),
            buildClassLabel(record) ? `Class: ${buildClassLabel(record)}` : '',
            `Strand: ${record.strand_name || 'N/A'}`,
            `Sub-strand: ${record.sub_strand_name || 'N/A'}`
        ]);
        const ctx = { doc, y: startY, margin, maxWidth };

        // Body Title
        doc.setTextColor(...BRAND_PRIMARY);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        const titleLines = doc.splitTextToSize(record.lesson_title || record.sub_strand_name || 'Record of Work', maxWidth);
        doc.text(titleLines, margin, ctx.y);
        ctx.y += (titleLines.length * 6) + 5;

        // Sections
        addSection(ctx, "Learning Outcomes", toOutcomeList(record.learning_outcomes));
        addSection(ctx, "Resources Used", sanitizeText(record.resources_used));
        addSection(ctx, "What Was Taught", sanitizeText(record.learning_activities) || 'No activities logged.');
        addSection(ctx, "Assessment Methods", sanitizeText(record.assessment_methods));
        addSection(ctx, "Teacher's Reflection", sanitizeText(record.remarks) || 'Reflection pending.');

        doc.save(`Record_of_Work_${record.lesson_date || 'Export'}.pdf`);
    } catch (err) {
        console.error("PDF Export Error: ", err);
        throw err;
    }
};

export const generateSOWPDF = async (scheme, roadmapEdits) => {
    try {
        const doc = new jsPDF('landscape');
        const pageWidth = doc.internal.pageSize.width;
        const margin = 14;
        
        const primaryColor = [24, 33, 109];
        const accentColor = [255, 196, 37];
        
        try {
            const logoBase64 = await getLogoBase64();
            if (logoBase64) {
                doc.addImage(logoBase64, 'PNG', margin, 10, 20, 20);
            }
        } catch (e) {
            console.error("Failed to load logo for PDF", e);
        }

        doc.setTextColor(...accentColor);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(20);
        doc.text("SCHEMES OF WORK", pageWidth - margin, 18, { align: 'right' });

        doc.setTextColor(...primaryColor);
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        const gradeName = scheme?.grade_level_name || scheme?.grade_level?.name || scheme?.learning_area?.grade_level_name || 'All Grades';
        const streamName = scheme?.stream_name || scheme?.stream?.name || scheme?.learning_area?.stream_name;
        const streamStr = streamName ? ` (${streamName})` : '';
        const termName = scheme?.term_name || scheme?.term?.name || scheme?.academic_term_name || 'Term';
        const gradeText = `${gradeName}${streamStr} - ${termName}`;
        doc.text(gradeText, pageWidth - margin, 26, { align: 'right' });

        doc.setTextColor(...primaryColor);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        const learningAreaText = scheme?.learning_area_name || scheme?.learning_area?.name || "Subject";
        doc.text(learningAreaText, pageWidth / 2, 38, { align: 'center' });
    
        const tableColumn = ["Week", "Strand", "Sub-Strand/Lesson", "Learning Outcomes", "Experiences", "Resources", "Enquiry", "Assessment"];
        const tableRows = [];

        const localLessonsByWeek = (roadmapEdits || []).reduce((acc, current) => {
            const weekNum = current.week_number || current.week || 1;
            if (!acc[weekNum]) acc[weekNum] = [];
            acc[weekNum].push(current);
            return acc;
        }, {});
        const localSortedWeeks = Object.keys(localLessonsByWeek).sort((a, b) => parseInt(a) - parseInt(b));

        localSortedWeeks.forEach(weekNum => {
            const lessons = localLessonsByWeek[weekNum] || [];
            lessons.forEach((lesson, index) => {
                const weekDisplay = index === 0 ? `Week ${weekNum}` : '';
                const strand = Array.isArray(lesson.strand) ? lesson.strand.join(', ') : (lesson.strand || '-');
                const subStrand = Array.isArray(lesson.sub_strand) ? lesson.sub_strand.join(', ') : (lesson.sub_strand || '-');
                const formatList = (arrOrStr) => {
                    if (Array.isArray(arrOrStr)) return arrOrStr.filter(Boolean).map(i => `• ${i}`).join('\n');
                    if (typeof arrOrStr === 'string' && arrOrStr.trim() !== '') {
                        return arrOrStr.split('\n').filter(i => i.trim()).map(i => `• ${i.trim().replace(/^•\s*/, '')}`).join('\n');
                    }
                    return '';
                };

                const combineLists = (list1, list2) => {
                    const formatted1 = formatList(list1);
                    const formatted2 = formatList(list2);
                    if (formatted1 && formatted2) return formatted1 + '\n' + formatted2;
                    return formatted1 || formatted2 || '-';
                };

                const outcomes = combineLists(lesson.specific_outcomes, lesson.imported_outcomes);
                const experiences = combineLists(lesson.learning_experiences, lesson.imported_experiences);
                const resources = Array.isArray(lesson.resources) ? lesson.resources.join('\n') : (lesson.resources || '-');
                const enquiry = combineLists(lesson.key_inquiry_questions, lesson.imported_inquiry_questions);
                const assessment = Array.isArray(lesson.assessment) ? lesson.assessment.join('\n') : (lesson.assessment || '-');

                tableRows.push([weekDisplay, strand, subStrand, outcomes, experiences, resources, enquiry, assessment]);
            });
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 45,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [24, 33, 109] },
            columnStyles: {
                0: { cellWidth: 15 },
                1: { cellWidth: 25 },
                2: { cellWidth: 30 },
                3: { cellWidth: 40 },
                4: { cellWidth: 40 },
                5: { cellWidth: 40 },
                6: { cellWidth: 40 },
                7: { cellWidth: 40 }
            }
        });

        doc.save(`SOW_${scheme?.learning_area_name || 'Subject'}_${scheme?.term_name || 'Term'}.pdf`);
    } catch (err) {
        console.error("PDF Export Error: ", err);
    }
};
